import { SEARCH_CHIP_COLORS } from 'sharedweb/helpers/shared-constants';
import {
    AlertsData, BroadCastMessages, OrderMessagesInterface, ProductConversionSummaryObj, SessionMessages, Notices
} from 'sharedweb/app.interface';
import { NanosecondsToMilliPipe } from 'sharedweb/pipes/nanoseconds-to-milli-pipe/nanoseconds-to-milli.pipe';
import { OrderStatusPipe } from 'sharedweb/pipes/acronym/acronym.pipe';
import { EsmnseToEsmPipe } from 'sharedweb/pipes/esmnse-to-esm/esmnse-to-esm.pipe';
import { Instrument, CustomDateTimeFormat, SocketErrorCodes } from 'sharedweb/app.structs';

/**
 * Shared function for generation of broker messages (Displayed in Toasts and saved in message bar as well).
 *
 * @export
 * @param AlertsData Alert Message Object received from socket.
 * @returns Parsed message string
 */
export function generateMessage(element: AlertsData): string {

    // Commenting below as per latest requirement, can be used in future.    
    // const depositKeyMessage = element.deposit_key ? ' for <strong>' + element.deposit_key.replace(/_ALL/g,'') + '</strong>': '';
    const depositKeyMessage = '';

    if (element.is_margin) {

        return `${element.client_code} has utilized <strong>${element.current_percentage}%</strong> of total margin <strong>${element.total}</strong> 
            (utilized margin <strong>${element.used}</strong>)
            ${depositKeyMessage}.
            Please contact Admin for further information.`;

    } else {
        if (element.is_reverted) {
            if (element.is_square_off) {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and the total margin available is
                    <strong>${element.total}</strong>(<strong>${element.current_percentage}%</strong>)${depositKeyMessage},
                    all applicable square-off restrictions
                    have been revoked.`;
            } else if (element.is_cancel) {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and the total margin available is <strong>${element.total}</strong>
                    (<strong>${element.current_percentage}%</strong>)${depositKeyMessage},
                    any applicable square-off restrictions have been revoked.`;
            } else {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and total margin available is
                    <strong>${element.total}</strong>(<strong>${element.current_percentage}%</strong>)${depositKeyMessage}`;
            }
        } else { // is not reverted
            if (element.is_square_off) {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and the total margin available is <strong>${element.total}</strong>
                    (<strong>${element.current_percentage}%</strong>)${depositKeyMessage},
                    which is less than the extreme stipulated limit.`;
            } else if (element.is_cancel) {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and the total margin available is <strong>${element.total}</strong>
                    (<strong>${element.current_percentage}%</strong>)${depositKeyMessage},
                    which is less than the minimum stipulated limit.
                    Applicable square-off restrictions have been imposed.`;
            } else {
                return `Client: ${element.client_code} The current MTM of your outstanding
                    positions is <strong>${element.used}</strong> and total margin available is
                    <strong>${element.total}</strong>(<strong>${element.current_percentage}%</strong>)${depositKeyMessage}`;
            }
        }
    }

}


export function parseBroadCastMessage(
    broadCastMessage: string,
    time: string
): BroadCastMessages {

    const p = broadCastMessage.split('">%1 : ')[1];
    broadCastMessage = p.split('</p>')[0];

    // Replaces http:// and https:// from the broadCastMessages
    // to make them External broadCastMessages as opposed to Internal broadCastMessages
    // broadCastMessage = broadCastMessage.replace('http://', '').replace('https://', '');
    broadCastMessage = broadCastMessage
    .replace(/href="/g, 'target="_blank" href="');

    let broadCastMessageArray = broadCastMessage.split("</a>");
    broadCastMessage = '';
    for(let i = 0; i < broadCastMessageArray.length;i++){
        if (!(broadCastMessageArray[i].includes('href="http://')) && !(broadCastMessageArray[i].includes('href="https://'))){
            broadCastMessageArray[i] = broadCastMessageArray[i].replace(/href="/g,'href="https://');
        }
    }
    broadCastMessage = broadCastMessageArray.join("</a>");

    return { message: broadCastMessage, time };
}

export function parseOrderMessageMobile(order: OrderMessagesInterface) {
    const nanoSecondPipe = new NanosecondsToMilliPipe();
    const orderStatusPipe = new OrderStatusPipe();
    const emsnseToEsmpipe = new EsmnseToEsmPipe();
    // As future/option and BSECM symbols scrip name doesn't contain series, so handling that.
    const scripNameString = (order.instrument_type === Instrument['Common Stock'] && order.market_id !== 'BSECM') ?
        order.scrip_name : `${order.scrip_name} ${order.series}`;
    return `${nanoSecondPipe.transform(order.modified, 'mediumTime')} | <strong> ${orderStatusPipe.transform(order.order_status)} </strong> |
    <span class="${order.order_mode}"> ${order.order_mode.toUpperCase()} </span> | ${emsnseToEsmpipe.transform(order.market_id)}:
     ${scripNameString} | <strong> ${order.quantity} @ ${order.price} </strong> | ${order.order_type.toUpperCase()} |
    <strong> ${order.order_validity.toUpperCase()} </strong> | ${order.primary_client_code}
    | ${order.exchange_order_id}`;
}

export function parseOrderMessageWeb(order: OrderMessagesInterface) {

    const nanoSecondPipe = new NanosecondsToMilliPipe();
    const orderStatusPipe = new OrderStatusPipe();
    const emsnseToEsmpipe = new EsmnseToEsmPipe();
    // As future/option and BSECM symbols scrip name doesn't contain series, so handling that.
    const scripNameString = (order.instrument_type === Instrument['Equity'] && order.market_id !== 'BSECM') ?
        order.scrip_name : `${order.scrip_name} ${order.series}`;
    return `${nanoSecondPipe.transform(order.modified, 'short')} | <strong> ${orderStatusPipe.transform(order.order_status)} </strong> |
    <span class="${order.order_mode}"> ${order.order_mode.toUpperCase()} </span> | ${emsnseToEsmpipe.transform(order.market_id)}:
     ${scripNameString} | <strong> ${order.quantity} @ ${order.price} </strong> | ${order.order_type.toUpperCase()} |
    <strong> ${order.order_validity.toUpperCase()} </strong> | ${order.primary_client_code}
    | ${order.exchange_order_id}`;
}

export function parseProductConversionRejectionMessage(data: ProductConversionSummaryObj[]) {
    const messages = [];
    data.forEach(summary => {
        if (!summary.rejection_text) { return; }
        const message = `Position conversion failed. Symbol: ${summary.scrip_name} Quantity: <strong>${summary.quantity}</strong>` +
            ` Source Product: <strong>${summary.source_product}</strong> Target Product: <strong>${summary.target_product}</strong>`;
        messages.push(message);
    });
    return messages;
}

export function parseSessionMessageMobile(data: SessionMessages) {
    const nanoSecondPipe = new NanosecondsToMilliPipe();
    let message = `<span class="notice-info"> ${nanoSecondPipe.transform(data.sessionTime, 'short')}</span> :&nbsp;
    <b>Exchange: </b>${data.exchangeId} <b>Market Type: </b>${data.marketType} <b>Current Session: </b>${data.currentSession}`;
    if (data.exchangeId === 'BSECD') {
        message += `<span>( <b>Product Id:</b>${data.productId} ) </span>`;
    }
    return message;
}

export function parseSessionMessageWeb(data: SessionMessages) {
    let message = `<b>Market Type: </b>${data.marketType} <b>
    Current Session: </b>${data.currentSession}`;
    if (data.exchangeId === 'BSECD') {
        message += `<span>( <b>Product Id:</b>${data.productId} ) </span>`;
    }
    return message;
}

export function parseNoticeMessageMobile(data: Notices) {
    const nanoSecondPipe = new NanosecondsToMilliPipe();
    return `<span class="notice-info font-16 font-bold">${nanoSecondPipe.transform(data.notice_time, 'short')} ${data.market_id}:</span>
    <span> ${data.text}</span>`;
}

export function parseNoticeMessageWeb(data: Notices) {
    return `<span class="notice-text">${data.text}</span>`;
}

export function parseErrorMessageWeb(errorCode, errorMsg) {
    if (errorCode === SocketErrorCodes.UserDoesNotExist) {
        const parts = errorMsg.split(',');
        if (parts.length >= 2) {
            const link = parts[0].trim();
            const phoneNumber = parts[1].trim();
            return `User does not exist. If you wish to apply for uTrade Algos, please click the below link: \
            <u>${link} </u> , For any assistance kindly contact support team at <u>${phoneNumber}</u>`;
        }
    } else if (errorCode === SocketErrorCodes.ExchangeNotMapped) {
        return `Have you just opened your account? Please wait for 48 hours post completion of account opening \
        formalities to get requisite approvals. In case it is taking too long, please contact support team at <u>${errorMsg}</u>`  
    } else if (errorCode === SocketErrorCodes.AccountNotActive) {
        return `Your account is inactive, have you just opened your account? Please wait 24 to 48 hours for activation. 
        In case it is taking too long, please contact support team at <u>${errorMsg}</u> for further assistance.`
    }
}
